import {Injectable, EventEmitter, Output} from '@angular/core';

import {BehaviorSubject} from 'rxjs/BehaviorSubject';
import {Observable} from 'rxjs/Observable';
import 'rxjs/add/operator/finally';

import {environment} from '../../../environments/environment';
import {DataService} from './data.service';

import {SingleDataLine, DataSeries, MeterData} from '../../shared/components/chart/meterData';

import {GetData} from '../../meter-usage-mock-data';
import {HttpHeaders} from '@angular/common/http';

import {SpinnerService} from './spinner.service';
import {UtilsService} from './utils.service';

@Injectable()
export class MeterUsageDataService {
    private useMockData = false;
    private debugVerbose = false;

    private mData: MeterData[];
    private meterData;

    constructor(private data: DataService,
                private spinnerService: SpinnerService,
                private utilsService: UtilsService
    ) {
        this.initBlankData();
        this.meterData = new BehaviorSubject<MeterData[]>(this.mData);
    }

    private initBlankData() {
        this.mData = [];
    }

    private getHeader(): HttpHeaders {
        let headers = new HttpHeaders();
        const programId = sessionStorage.getItem('currentProgram');
        this.utilsService.debugLog(programId);
        if (programId) {
            headers = headers.set('Program', programId);
        }

        return headers;
    }

    loadMeterUsageData(subId: string, level: string, startDate: string, endDate: string, product: string) {
        if (!this.useMockData) {
            let url = environment.meter_usage_url;
            url = url + '?level=' + level + '&start_datetime=' + startDate + '&end_datetime=' + endDate+ '&product=' + product;
            if (subId.length > 0) {
                url = url + '&portfolio_id=' + subId;
            }

            this.utilsService.debugLog('Invoking ' + url);
            // this.spinnerService.show('meterSpinner');
            this.data.get(url, this.getHeader())
                .finally(() => {
                    // this.spinnerService.hide('meterSpinner');
                }).map((response) => {
                    if (response) {
                        if (this.debugVerbose) {
                            this.utilsService.debugLog('received meter response ', response);
                        }
                        const latestData: MeterData = this.decodeMeterDataResponse(response['data'], product);
                        let found = false;
                        for (const md of this.mData) {
                            if (latestData.endTime.getTime() === md.endTime.getTime()
                                && latestData.startTime.getTime() === md.startTime.getTime()
                                && latestData.level === md.level
                                && latestData.loadedPortfolioID === md.loadedPortfolioID
                                && latestData.product === md.product) {
                                // update with data from the newly read series
                                md.dataSeries = latestData.dataSeries;
                                md.dataLoadTimestamp = latestData.dataLoadTimestamp;
                                found = true;
                            }
                        }
                        if (!found) {
                            this.mData.push(latestData);
                        }

                        if (this.debugVerbose) {
                            this.utilsService.debugLog('decoded meter response');
                            this.utilsService.debugLog(latestData);
                        }

                        if (this.mData.length > 3) {
                            // remove all but newest 3
                            this.mData = this.mData.sort((a, b) => b.dataLoadTimestamp - a.dataLoadTimestamp).slice(0, 3);
                        }

                        if (this.debugVerbose) {
                            this.utilsService.debugLog('meter data array');
                            this.utilsService.debugLog(this.mData);
                        }
                    }
                }).subscribe(data => {
                    // do something, if upload success
                    this.meterData.next(this.mData);
                }, error => {
                    // todo handle error
                });
        } else {
            if (this.mData.length === 0) {
                this.mData.push(GetData());
            }
            this.meterData.next(this.mData);
        }

    }

    getMeterDataArray(): Observable<MeterData[]> {
        return this.meterData.asObservable();
    }

    private decodeMeterDataResponse(dataResponse, product: string): MeterData {
        if (this.debugVerbose) {
            this.utilsService.debugLog('decoding meter response');
        }

        const meterData: MeterData = {
            loadedPortfolioID: '',
            startTime: null,
            endTime: null,
            level: '',
            dataSeries: [],
            dataLoadTimestamp: Date.now(),
            product: null
        }

        meterData.loadedPortfolioID = dataResponse.portfolio_id;
        meterData.startTime = new Date(dataResponse.start_date);
        meterData.endTime = new Date(dataResponse.end_date);
        meterData.level = dataResponse.level.toUpperCase();
        meterData.product = product;
        for (const dataSeries of dataResponse.data_series) {
            const meterDataSeries: DataSeries = {
                label: dataSeries.name.trim(),
                colour: dataSeries.colour.trim(),
                stepped: false, // meter data should be spline
                intervals: [],
                datalines: [],
                collatedSeries: []
            };

            const max_timescale = dataSeries.timescale.length;

            if (this.debugVerbose) {
                this.utilsService.debugLog('max_timescale = ' + max_timescale);
            }

            for (let i = 0; i < max_timescale; i++) {
                meterDataSeries.intervals.push(new Date());
            }

            if (this.debugVerbose) {
                this.utilsService.debugLog('decoding dates');
            }

            for (const timescale of dataSeries.timescale) {
                meterDataSeries.intervals[timescale.interval_id - 1] = new Date(timescale.interval_time);
            }

            if (this.debugVerbose) {
                this.utilsService.debugLog('constructing datalines');
            }

            for (const series of dataSeries.series) {
                const dataline: SingleDataLine = {
                    id: series.id,
                    dataValues: []
                }

                if (this.debugVerbose) {
                    this.utilsService.debugLog('preparing datavalues');
                }

                for (let i = 0; i < max_timescale; i++) {
                    dataline.dataValues.push(null);
                }

                if (this.debugVerbose) {
                    this.utilsService.debugLog('setting datavalues');
                }

                for (const dataValue of series.data) {
                    dataline.dataValues[dataValue.interval_id - 1] = dataValue.value;
                }

                if (this.debugVerbose) {
                    this.utilsService.debugLog('saving dataline');
                }

                meterDataSeries.datalines.push(dataline);
            }

            if (this.debugVerbose) {
                this.utilsService.debugLog('adding dataseries');
            }

            meterData.dataSeries.push(meterDataSeries);
        }

        if (this.debugVerbose) {
            this.utilsService.debugLog('decoded data: ');
        }
        if (this.debugVerbose) {
            this.utilsService.debugLog(meterData);
        }

        return meterData;
    }

}



// WEBPACK FOOTER //
// ./src/app/core/services/meter-usage-data.service.ts